import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Grid, Paper, Stack, IconButton, Tooltip, 
    ToggleButton, ToggleButtonGroup, CircularProgress, Fade, Divider,
    Menu, MenuItem, TextField, Select, FormControl, InputLabel
} from '@mui/material';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import {
    BarChart as BarChartIcon,
    PieChart as PieChartIcon,
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
    FileDownload as DownloadIcon,
    TrendingUp as TrendingIcon
} from '@mui/icons-material';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
    Tooltip as ChartTooltip, Legend, ResponsiveContainer, LabelList
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppHeader from '../../components/AppHeader';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Only render label if the slice is larger than 4% to prevent overlaps
    if (percent < 0.04) return null;

    return (
        <text 
            x={x} 
            y={y} 
            fill="#ffffff" 
            textAnchor="middle" 
            dominantBaseline="central"
            style={{ 
                fontSize: '10px', 
                fontWeight: '800', 
                textShadow: '0px 1px 3px rgba(0,0,0,0.6)',
                pointerEvents: 'none'
            }}
        >
            {`${name} (${(percent * 100).toFixed(0)}%)`}
        </text>
    );
};

const DemographicAnalytics = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
    
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [chartType, setChartType] = useState('pie'); // 'pie' or 'bar'

    // Filter States
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [presetRange, setPresetRange] = useState("this_year");

    const handlePresetChange = (preset) => {
        setPresetRange(preset);
        if (preset === 'custom') return;

        const today = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const toKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

        if (preset === 'today') {
            setDateFrom(toKey(today));
            setDateTo(toKey(today));
        } else if (preset === 'yesterday') {
            const yesterday = new Date();
            yesterday.setDate(today.getDate() - 1);
            setDateFrom(toKey(yesterday));
            setDateTo(toKey(yesterday));
        } else if (preset === 'this_week') {
            const currentDay = today.getDay();
            const distanceToMonday = currentDay === 0 ? -6 : 1 - currentDay;
            const monday = new Date(today);
            monday.setDate(today.getDate() + distanceToMonday);

            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);

            setDateFrom(toKey(monday));
            setDateTo(toKey(sunday));
        } else if (preset === 'this_month') {
            const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
            setDateFrom(toKey(firstDay));
            setDateTo(toKey(today));
        } else if (preset === 'last_month') {
            const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            setDateFrom(toKey(firstDayLastMonth));
            setDateTo(toKey(lastDayLastMonth));
        } else if (preset === 'this_year') {
            const firstDayYear = new Date(today.getFullYear(), 0, 1);
            setDateFrom(toKey(firstDayYear));
            setDateTo(toKey(today));
        }
    };

    // Initialize date range on mount
    useEffect(() => {
        const pad = (n) => String(n).padStart(2, '0');
        const toKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

        const now = new Date();
        const firstDayYear = new Date(now.getFullYear(), 0, 1);

        setDateFrom(toKey(firstDayYear));
        setDateTo(toKey(now));
    }, []);

    const fetchData = useCallback(async () => {
        if (!dateFrom || !dateTo) return;
        setLoading(true);
        try {
            const h = { 'X-Tenant': tenant };
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/web-reports/demographics`, { 
                params: {
                    date_from: dateFrom,
                    date_to: dateTo
                },
                withCredentials: true, 
                headers: h 
            });
            if (res.data.success) {
                setData(res.data.data);
            }
        } catch (err) {
            console.error("Demographics fetch error:", err);
        } finally {
            setLoading(false);
        }
    }, [tenant, dateFrom, dateTo]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const exportToExcel = () => {
        if (!data) return;

        try {
            const wb = XLSX.utils.book_new();

            // 1. Gender Distribution
            const genderSheetData = (data.gender || []).map(r => ({
                "Gender": r.name || "Unknown",
                "Count": Number(r.value || 0)
            }));
            const genderWs = XLSX.utils.json_to_sheet(genderSheetData);
            XLSX.utils.book_append_sheet(wb, genderWs, "Gender Distribution");

            // 2. Marital Status
            const maritalSheetData = (data.marital || []).map(r => ({
                "Marital Status": r.name || "Unknown",
                "Count": Number(r.value || 0)
            }));
            const maritalWs = XLSX.utils.json_to_sheet(maritalSheetData);
            XLSX.utils.book_append_sheet(wb, maritalWs, "Marital Status");

            // 3. Age Groupings
            const ageSheetData = (data.age || []).map(r => ({
                "Age Group": r.name || "Unknown",
                "Count": Number(r.value || 0)
            }));
            const ageWs = XLSX.utils.json_to_sheet(ageSheetData);
            XLSX.utils.book_append_sheet(wb, ageWs, "Age Groupings");

            // 4. Service Seniority
            const serviceSheetData = (data.service || []).map(r => ({
                "Seniority Bracket": r.name || "Unknown",
                "Count": Number(r.value || 0)
            }));
            const serviceWs = XLSX.utils.json_to_sheet(serviceSheetData);
            XLSX.utils.book_append_sheet(wb, serviceWs, "Service Seniority");

            // 5. Religious Diversity
            const religionSheetData = (data.religion || []).map(r => ({
                "Religion": r.name || "Unknown",
                "Count": Number(r.value || 0)
            }));
            const religionWs = XLSX.utils.json_to_sheet(religionSheetData);
            XLSX.utils.book_append_sheet(wb, religionWs, "Religious Diversity");

            // 6. Ethnic Background
            const raceSheetData = (data.race || []).map(r => ({
                "Ethnicity": r.name || "Unknown",
                "Count": Number(r.value || 0)
            }));
            const raceWs = XLSX.utils.json_to_sheet(raceSheetData);
            XLSX.utils.book_append_sheet(wb, raceWs, "Ethnic Background");

            XLSX.writeFile(wb, `Demographic_Analytics_Report_${dateFrom}_to_${dateTo}.xlsx`);
        } catch (error) {
            console.error("Excel export error:", error);
            alert("Failed to export Excel report");
        }
    };

    const renderChart = (chartData, title) => {
        if (!chartData || chartData.length === 0) return (
            <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>No data available for {title}</Typography>
            </Box>
        );

        return (
            <Box sx={{ height: 480, width: '100%', mt: 2 }}>
                <ResponsiveContainer>
                    {chartType === 'pie' ? (
                        <PieChart style={{ overflow: 'visible' }} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="43%"
                                innerRadius={95}
                                outerRadius={150}
                                paddingAngle={2}
                                dataKey="value"
                                labelLine={false}
                                label={renderCustomizedLabel}
                                animationBegin={0}
                                animationDuration={1000}
                            >
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <ChartTooltip 
                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '12px' }}
                            />
                            <Legend 
                                verticalAlign="bottom" 
                                align="center" 
                                iconType="circle"
                                wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
                            />
                        </PieChart>
                    ) : (
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                            <ChartTooltip 
                                cursor={{ fill: '#f8fafc' }}
                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '12px' }}
                            />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]} animationDuration={1000}>
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                                <LabelList dataKey="value" position="top" style={{ fontSize: 11, fontWeight: 700, fill: '#475569' }} />
                            </Bar>
                        </BarChart>
                    )}
                </ResponsiveContainer>
            </Box>
        );
    };

    const exportChart = async (chartTitle, format) => {
        const cardId = `chart-card-${chartTitle.replace(/\s+/g, '-').toLowerCase()}`;
        const element = document.getElementById(cardId);
        if (!element) return;

        try {
            const canvas = await html2canvas(element, {
                backgroundColor: '#ffffff',
                scale: 2,
                logging: false,
                useCORS: true
            });

            const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
            const fileExtension = format === 'jpeg' ? 'jpg' : 'png';
            
            const dataUrl = canvas.toDataURL(mimeType, 1.0);
            const link = document.createElement('a');
            link.download = `${chartTitle.replace(/\s+/g, '_')}.${fileExtension}`;
            link.href = dataUrl;
            link.click();
        } catch (error) {
            console.error('Error exporting chart:', error);
            alert('Failed to export chart image');
        }
    };

    const AnalyticsCard = ({ title, data, icon: Icon, color }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);

        const handleClick = (event) => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        const handleExport = (format) => {
            handleClose();
            exportChart(title, format);
        };

        const cardId = `chart-card-${title.replace(/\s+/g, '-').toLowerCase()}`;

        return (
            <Paper 
                id={cardId}
                sx={{ 
                    p: 3, borderRadius: 5, height: '100%', 
                    border: '1px solid #e2e8f0', 
                    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.02)',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)' }
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${color}15`, color: color, display: 'flex' }}>
                            <Icon fontSize="small" />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1rem' }}>{title}</Typography>
                    </Stack>
                    
                    {/* Download options */}
                    <Box data-html2canvas-ignore="true">
                        <IconButton
                            size="small"
                            onClick={handleClick}
                            sx={{ color: '#64748b', '&:hover': { color: '#334155', bgcolor: '#f1f5f9' } }}
                        >
                            <DownloadIcon fontSize="small" />
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            PaperProps={{
                                sx: {
                                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.05)',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 3
                                }
                            }}
                        >
                            <MenuItem onClick={() => handleExport('png')}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>Download PNG</Typography>
                            </MenuItem>
                            <MenuItem onClick={() => handleExport('jpeg')}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>Download JPEG</Typography>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Stack>
                <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />
                {renderChart(data, title)}
            </Paper>
        );
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            
            <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1600, mx: 'auto' }}>
                {/* Header Section */}
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 4 }}>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <IconButton onClick={() => navigate('/reports')} size="small" sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em' }}>Demographic Analytics</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ color: '#64748b', ml: 5 }}>Comprehensive workforce diversity and organizational breakdown.</Typography>
                    </Box>

                    <Stack direction="row" spacing={2} alignItems="center">
                        <ToggleButtonGroup
                            value={chartType}
                            exclusive
                            onChange={(e, v) => v && setChartType(v)}
                            size="small"
                            sx={{ bgcolor: '#fff', borderRadius: 2, '& .Mui-selected': { bgcolor: '#f1f5f9 !important', color: '#6366f1 !important' } }}
                        >
                            <ToggleButton value="pie" sx={{ px: 2 }}><PieChartIcon fontSize="small" sx={{ mr: 1 }} /> Pie</ToggleButton>
                            <ToggleButton value="bar" sx={{ px: 2 }}><BarChartIcon fontSize="small" sx={{ mr: 1 }} /> Bar</ToggleButton>
                        </ToggleButtonGroup>

                        <Tooltip title="Export to Excel">
                            <IconButton 
                                onClick={exportToExcel}
                                disabled={!data}
                                sx={{ bgcolor: '#10b981', color: '#fff', '&:hover': { bgcolor: '#059669' } }}
                            >
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* Filters Panel */}
                <Paper
                    sx={{
                        p: 2.5,
                        borderRadius: 4,
                        mb: 4,
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.02)',
                        bgcolor: '#fff'
                    }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Quick Range</InputLabel>
                                <Select
                                    value={presetRange}
                                    label="Quick Range"
                                    onChange={(e) => handlePresetChange(e.target.value)}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="custom">Custom Range</MenuItem>
                                    <MenuItem value="today">Today</MenuItem>
                                    <MenuItem value="yesterday">Yesterday</MenuItem>
                                    <MenuItem value="this_week">This Week</MenuItem>
                                    <MenuItem value="this_month">This Month</MenuItem>
                                    <MenuItem value="last_month">Last Month</MenuItem>
                                    <MenuItem value="this_year">This Year</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3.5 }}>
                            <TextField
                                type="date"
                                label="Date From"
                                size="small"
                                fullWidth
                                value={dateFrom}
                                onChange={(e) => {
                                    setDateFrom(e.target.value);
                                    setPresetRange('custom');
                                }}
                                InputLabelProps={{ shrink: true }}
                                slotProps={{ input: { sx: { borderRadius: 2 } } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3.5 }}>
                            <TextField
                                type="date"
                                label="Date To"
                                size="small"
                                fullWidth
                                value={dateTo}
                                onChange={(e) => {
                                    setDateTo(e.target.value);
                                    setPresetRange('custom');
                                }}
                                InputLabelProps={{ shrink: true }}
                                slotProps={{ input: { sx: { borderRadius: 2 } } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <IconButton 
                                onClick={fetchData} 
                                sx={{ 
                                    bgcolor: 'primary.main', 
                                    color: '#fff', 
                                    '&:hover': { bgcolor: 'primary.dark' },
                                    width: '100%',
                                    borderRadius: 2,
                                    py: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    gap: 1
                                }}
                            >
                                <RefreshIcon fontSize="small" /> Refresh
                            </IconButton>
                        </Grid>
                    </Grid>
                </Paper>

                {loading ? (
                    <Box sx={{ height: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={40} sx={{ color: 'primary.main' }} />
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>Analyzing workforce data...</Typography>
                    </Box>
                ) : (
                    <Fade in timeout={800}>
                        <Grid container spacing={3}>
                            {/* Main Metrics Row */}
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <AnalyticsCard title="Gender Distribution" data={data?.gender} icon={TrendingIcon} color="primary.main" />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <AnalyticsCard title="Marital Status" data={data?.marital} icon={TrendingIcon} color="#ec4899" />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <AnalyticsCard title="Age Groupings" data={data?.age} icon={TrendingIcon} color="#f59e0b" />
                            </Grid>

                            {/* Secondary Metrics Row */}
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <AnalyticsCard title="Service Seniority" data={data?.service} icon={TrendingIcon} color="#10b981" />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <AnalyticsCard title="Religious Diversity" data={data?.religion} icon={TrendingIcon} color="#8b5cf6" />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <AnalyticsCard title="Ethnic Background" data={data?.race} icon={TrendingIcon} color="#f97316" />
                            </Grid>
                        </Grid>
                    </Fade>
                )}
            </Box>
        </Box>
    );
};

export default DemographicAnalytics;
